import type { ComponentProps } from "react";
import { createContext, useContext } from "react";
import { tv } from "tailwind-variants";

import type { DraftCartItem } from "@/domains/careplan/models/types";
import { Card, Counter, Heading, type HeadingProps } from "@/ui";

const cartProductVariants = tv({
  slots: {
    root: undefined,
    name: "text-base font-semibold sm:text-base",
    image:
      "h-48 w-full rounded-2xl bg-product-media object-contain sm:aspect-square sm:h-auto sm:w-60 sm:object-cover",
    price: "flex items-center gap-1 text-xl font-semibold text-content-secondary sm:text-2xl",
    quantity: "text-sm",
    shippingFrequency: "text-sm font-semibold",
  },
  variants: {
    noQuantity: {
      true: {
        root: "border-0",
      },
      false: {
        root: "border",
      },
    },
  },
});

const { image, name, price, quantity, root, shippingFrequency } = cartProductVariants();

export type CartProductProps = {
  product: DraftCartItem;
} & ComponentProps<"div">;

type CartProductContextType = DraftCartItem;

const CartProductContext = createContext<CartProductContextType | null>(null);

export const useCartProductContext = () => {
  const product = useContext(CartProductContext);
  if (!product) {
    throw Error("useCartProductContext can only be used inside a CartProductContext.Provider");
  }

  return product;
};

const Name = ({ className, ...props }: HeadingProps) => {
  const product = useCartProductContext();

  return (
    <Heading className={name({ className })} {...props}>
      {product.shortName}
    </Heading>
  );
};

const Price = ({ className, ...props }: ComponentProps<"p">) => {
  const product = useCartProductContext();

  const displayPrice =
    product.source === "subscription" ? product.price : product.originalPriceAsString;

  return (
    <p className={price({ className })} {...props}>
      ${displayPrice}
    </p>
  );
};

type QuantityProps = Omit<ComponentProps<"p">, "onChange"> & {
  disabled?: boolean;
  onChange: (quantity: number) => void;
};

const Quantity = ({ className, disabled, onChange, ...props }: QuantityProps) => {
  const product = useCartProductContext();

  return (
    <div className="flex items-center gap-4">
      <p className={quantity({ className })} {...props}>
        Quantity:
      </p>
      <Counter disabled={disabled} onChange={onChange} value={product.quantity} />
    </div>
  );
};

const ShippingFrequency = ({ className, ...props }: ComponentProps<"p">) => {
  return (
    <p className={shippingFrequency({ className })} {...props}>
      Ships monthly
    </p>
  );
};

const Image = ({ className }: ComponentProps<"img">) => {
  const product = useCartProductContext();

  return <img alt={product.name} className={image({ className })} src={product.image} />;
};

const Root = ({ children, className, product }: CartProductProps) => {
  return (
    <CartProductContext.Provider value={product}>
      <Card className={root({ className, noQuantity: product.quantity === 0 })}>{children}</Card>
    </CartProductContext.Provider>
  );
};

export const CartProduct = {
  Root,
  Name,
  Price,
  Image,
  Quantity,
  ShippingFrequency,
};
